import React from 'react';
import { motion } from 'framer-motion';
import { Result } from '../../../../services/serpApi';
import RatingStars from '../../../LeadsSalvos/components/LeadsTable/RatingStars/RatingStars';
import { useTranslation } from 'react-i18next';
import { getWhatsAppLink } from '../../../../utils/whatsappUtils';
import { useSearchPersistence } from '../../../../hooks/useSearchPersistence';

interface ProspectDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  prospect: Result | null;
}

const ProspectDetailModal: React.FC<ProspectDetailModalProps> = ({
  isOpen,
  onClose,
  prospect,
}) => {
  const { t } = useTranslation();
  const { searchContext } = useSearchPersistence();

  if (!isOpen || !prospect) return null;

  const dataItems = [
    { label: t('dashboard.detail_company'), value: prospect.empresa },
    { label: t('dashboard.detail_contact'), value: prospect.contato },
    { label: t('dashboard.detail_website'), value: prospect.website, isLink: true },
    { label: t('dashboard.detail_phone'), value: prospect.phone },
    { label: t('dashboard.detail_location'), value: prospect.localizacao, fullWidth: true },
    { label: t('dashboard.detail_whatsapp_status'), value: t(`dashboard.status_${prospect.status === 'Validado' ? 'validated' : prospect.status === 'Pendente' ? 'pending' : 'invalid'}`) },
    { label: t('dashboard.detail_prospected'), value: prospect.prospected ? t('dashboard.prospected_yes') : t('dashboard.prospected_no') },
    { label: t('dashboard.detail_reviews'), value: prospect.reviews ? `${prospect.reviews} ${t('Resenha', { count: prospect.reviews })}` : 'N/A' },
  ];

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        className="relative w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700/50 p-6 flex flex-col gap-6 max-h-[90vh] overflow-y-auto"
        initial={{ y: 50, opacity: 0, scale: 0.95 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 50, opacity: 0, scale: 0.95 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-200 dark:border-gray-700/50 pb-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-bold text-[#181411] dark:text-white break-words">{prospect.empresa}</h2>
            <div className="flex items-center gap-2 mt-1">
              <RatingStars rating={Math.round(prospect.rating || 0)} />
              <span className="text-sm text-gray-500 dark:text-gray-400">({prospect.rating?.toFixed(1) || 'N/A'})</span>
            </div>
          </div>
          <motion.button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10"
            whileHover={{ rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">close</span>
          </motion.button>
        </div>

        {/* Body - Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {dataItems.map((item, index) => (
            <div key={index} className={item.fullWidth ? 'col-span-2' : 'col-span-1'}>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{item.label}</p>
              {item.isLink && item.value ? (
                <a
                  href={`http://${item.value}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline break-words"
                >
                  {item.value}
                </a>
              ) : (
                <p className="text-sm text-[#181411] dark:text-white break-words">{item.value || 'N/A'}</p>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700/50">
          {prospect.phone && getWhatsAppLink(prospect.phone, searchContext?.countryCode) && (
            <motion.a
              href={getWhatsAppLink(prospect.phone, searchContext?.countryCode) || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-10 items-center gap-2 cursor-pointer justify-center overflow-hidden rounded-lg bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 text-white text-sm font-bold px-4 transition-colors flex-shrink-0"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/whatsapp-logo.png`}
                alt="WhatsApp"
                className="w-5 h-5 flex-shrink-0"
              />
              {t('Enviar mensagem')}
            </motion.a>
          )}
          <motion.button
            onClick={onClose}
            className="flex h-10 w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-700/50 text-[#181411] dark:text-gray-300 text-sm font-bold px-4 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('dashboard.detail_close')}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ProspectDetailModal;